import { useEffect } from 'react'

interface PageMetadata {
  title: string
  description: string
  path?: string
  locale?: string
  type?: 'website' | 'article' | 'product'
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>
}

const setMetaTag = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector)

  if (!element) {
    element = document.createElement('meta')
    document.head.appendChild(element)
  }

  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value))
}

const setLinkTag = (selector: string, rel: string, href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>(selector)

  if (!element) {
    element = document.createElement('link')
    element.rel = rel
    document.head.appendChild(element)
  }

  element.href = href
}

const setStructuredData = (structuredData?: PageMetadata['structuredData']) => {
  const selector = 'script[data-page-structured-data="true"]'
  const existing = document.head.querySelector<HTMLScriptElement>(selector)

  if (!structuredData) {
    existing?.remove()
    return
  }

  const element = existing ?? document.createElement('script')
  element.type = 'application/ld+json'
  element.dataset.pageStructuredData = 'true'
  element.textContent = JSON.stringify(structuredData)

  if (!existing) {
    document.head.appendChild(element)
  }
}

export const usePageMetadata = ({
  title,
  description,
  path = '/',
  locale,
  type = 'website',
  structuredData,
}: PageMetadata) => {
  const structuredDataKey = JSON.stringify(structuredData ?? null)

  useEffect(() => {
    const url = new URL(path, window.location.origin).toString()

    document.title = title
    setMetaTag('meta[name="description"]', {
      name: 'description',
      content: description,
    })
    setMetaTag('meta[property="og:title"]', {
      property: 'og:title',
      content: title,
    })
    setMetaTag('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    })
    setMetaTag('meta[property="og:type"]', {
      property: 'og:type',
      content: type,
    })
    setMetaTag('meta[property="og:url"]', {
      property: 'og:url',
      content: url,
    })
    setMetaTag('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary_large_image',
    })

    if (locale) {
      setMetaTag('meta[property="og:locale"]', {
        property: 'og:locale',
        content: locale,
      })
    }

    setLinkTag('link[rel="canonical"]', 'canonical', url)
    setStructuredData(structuredData)
  }, [title, description, path, locale, type, structuredDataKey, structuredData])
}